(() => {
  'use strict';

  const roots = document.querySelectorAll('[data-f2w-event-directory]');
  if (!roots.length) return;

  const GRAPHQL_QUERY = `
    query FightBaseEvents($after: String) {
      metaobjects(type: "event", first: 250, after: $after) {
        nodes {
          id
          handle
          onlineStoreUrl
          fields { key value }
        }
        pageInfo { hasNextPage endCursor }
      }
    }
  `;

  const INDEX_EVENT_QUERY = `
    query FightBaseIndexedEvents($ids: [ID!]!) {
      nodes(ids: $ids) {
        __typename
        ... on Metaobject {
          id
          handle
          onlineStoreUrl
          fields { key value }
        }
      }
    }
  `;

  const REFERENCE_QUERY = `
    query FightBaseEventReferences($ids: [ID!]!) {
      nodes(ids: $ids) {
        __typename
        ... on Metaobject {
          id
          handle
          fields { key value }
        }
        ... on MediaImage {
          id
          image { url }
        }
      }
    }
  `;

  const normalize = (value) => String(value || '')
    .toLocaleLowerCase('nl-NL')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();

  const escapeHtml = (value) => String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  const countryFlag = (code, label) => {
    const flagCode = String(code || '').trim().toLowerCase();
    const valid = flagCode.length === 2 || ['gb-sct', 'gb-eng', 'gb-wls'].includes(flagCode);
    if (!valid) return '';
    return `<span class="f2w-country-flag"><img src="https://flagcdn.com/w40/${escapeHtml(flagCode)}.png" srcset="https://flagcdn.com/w80/${escapeHtml(flagCode)}.png 2x" alt="${escapeHtml(label)}" width="20" height="15" loading="lazy"></span>&nbsp;`;
  };

  const parseBoolean = (value) => ['true', '1', 'yes'].includes(normalize(value));

  const parseDate = (value) => {
    const match = String(value || '').match(/^\d{4}-\d{2}-\d{2}/);
    return match ? match[0] : '';
  };

  const parseReferenceIds = (value) => {
    if (!value) return [];
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return parsed.filter(Boolean);
      if (typeof parsed === 'string') return [parsed];
    } catch (error) {
      // A single metaobject reference is returned as a plain GID.
    }
    return String(value).split(',').map((item) => item.trim()).filter(Boolean);
  };

  const fieldMap = (fields) => (fields || []).reduce((map, field) => {
    map[field.key] = field.value;
    return map;
  }, {});

  const filterLabel = (value) => {
    const text = String(value || '').trim();
    return text
      ? text.charAt(0).toLocaleUpperCase('nl-NL') + text.slice(1)
      : text;
  };

  const titleCaseWords = (value) => String(value || '')
    .trim()
    .split(/\s+/)
    .map((word) => word
      .split(/([-'])/)
      .map((part) => /^[a-zà-ÿ]/i.test(part)
        ? part.charAt(0).toLocaleUpperCase('nl-NL') + part.slice(1)
        : part)
      .join(''))
    .join(' ');

  const uniqueSorted = (values, labeler = filterLabel) => {
    const unique = new Map();
    values.filter(Boolean).forEach((value) => {
      const label = labeler(value);
      const key = normalize(label);
      if (key && !unique.has(key)) unique.set(key, label);
    });
    return [...unique.values()].sort((a, b) => a.localeCompare(b, 'nl-NL'));
  };

  const sortEventsChronologically = (events) => [...events].sort((a, b) => {
    if (!a.dateStart && !b.dateStart) return 0;
    if (!a.dateStart) return 1;
    if (!b.dateStart) return -1;
    return a.dateStart.localeCompare(b.dateStart);
  });

  const today = () => {
    const now = new Date();
    return [now.getFullYear(), String(now.getMonth() + 1).padStart(2, '0'), String(now.getDate()).padStart(2, '0')].join('-');
  };

  const fetchGraphQL = async (root, query, variables) => {
    const token = root.dataset.storefrontToken;
    const shopDomain = root.dataset.shopDomain;
    const apiVersion = root.dataset.apiVersion || '2024-10';
    if (!token || !shopDomain) throw new Error('FightBase event API configuration is incomplete.');

    const response = await fetch(`https://${shopDomain}/api/${apiVersion}/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept-Language': root.dataset.language || 'nl',
        'X-Shopify-Storefront-Access-Token': root.dataset.storefrontToken
      },
      body: JSON.stringify({ query, variables })
    });

    if (!response.ok) throw new Error(`FightBase event API returned ${response.status}.`);
    const payload = await response.json();
    if (payload.errors?.length) throw new Error(payload.errors.map((error) => error.message).join('; '));
    return payload.data;
  };

  const readIndexEventIds = (root) => {
    const script = root.querySelector('[data-f2w-event-index-json]');
    if (!script) return null;

    const raw = script.textContent.trim();
    if (!raw) return null;

    try {
      let payload = JSON.parse(raw);
      if (typeof payload === 'string') payload = JSON.parse(payload);
      if (!payload || payload.v !== 1 || !Array.isArray(payload.rows) || payload.count !== payload.rows.length) {
        throw new Error('The published Event index shape is invalid.');
      }

      const ids = payload.rows
        .map((row) => Array.isArray(row) ? row[0] : '')
        .filter((id) => /^gid:\/\/shopify\/Metaobject\/[0-9]+$/.test(String(id || '')));
      if (new Set(ids).size !== ids.length || ids.length !== payload.count) {
        throw new Error('The published Event index contains invalid or duplicate IDs.');
      }
      return ids;
    } catch (error) {
      console.warn('[FightBase] Ignoring the published Event index.', error);
      return null;
    }
  };

  const loadIndexedEvents = async (root, ids) => {
    const events = [];
    for (let index = 0; index < ids.length; index += 250) {
      const data = await fetchGraphQL(root, INDEX_EVENT_QUERY, { ids: ids.slice(index, index + 250) });
      const nodes = (data?.nodes || []).filter((node) => node?.__typename === 'Metaobject' && node.id);
      events.push(...nodes);
    }
    if (events.length !== ids.length) throw new Error('The published Event index could not be fully hydrated.');
    return events;
  };

  const loadAllEvents = async (root) => {
    const events = [];
    let after = null;
    let hasNextPage = true;

    while (hasNextPage) {
      const data = await fetchGraphQL(root, GRAPHQL_QUERY, { after });
      const connection = data?.metaobjects;
      if (!connection) throw new Error('The event metaobject connection was not returned.');
      events.push(...(connection.nodes || []));
      hasNextPage = Boolean(connection.pageInfo?.hasNextPage);
      after = connection.pageInfo?.endCursor || null;
      if (hasNextPage && !after) throw new Error('The event cursor was not returned.');
    }

    return events;
  };

  const hydrateReferences = async (root, events) => {
    const ids = new Set();
    const referenceKeys = root.dataset.isArchive === 'true'
      ? ['country', 'organization', 'sports', 'fighters', 'venue']
      : ['country', 'organization', 'sports', 'venue'];
    events.forEach((event) => {
      const fields = fieldMap(event.fields);
      referenceKeys.forEach((key) => {
        parseReferenceIds(fields[key]).forEach((id) => ids.add(id));
      });
    });

    if (!ids.size) return new Map();

    const references = new Map();
    const idList = [...ids];
    for (let index = 0; index < idList.length; index += 250) {
      const data = await fetchGraphQL(root, REFERENCE_QUERY, { ids: idList.slice(index, index + 250) });
      (data?.nodes || []).forEach((node) => {
        if (node?.id) references.set(node.id, node);
      });
    }
    return references;
  };

  const referenceName = (id, references) => {
    const reference = references.get(id);
    if (!reference) return '';
    const fields = fieldMap(reference.fields);
    return fields.name || fields.title || reference.handle || '';
  };

  const referenceNames = (value, references) => parseReferenceIds(value)
    .map((id) => referenceName(id, references))
    .filter(Boolean);

  const referenceOrValue = (value, references) => {
    const text = String(value || '').trim();
    const resolved = parseReferenceIds(value)
      .map((id) => referenceName(id, references))
      .find(Boolean);
    if (resolved) return resolved;
    if (/^gid:\/\/shopify\/Metaobject\//.test(text) || text.startsWith('[')) return '';
    return text;
  };

  const referenceCode = (value, references) => {
    const id = parseReferenceIds(value)[0];
    if (!id) return '';
    const fields = fieldMap(references.get(id)?.fields);
    return fields.code || '';
  };

  const referenceImage = (value, references) => {
    for (const id of parseReferenceIds(value)) {
      const image = references.get(id)?.image?.url;
      if (image) return image;
    }
    return '';
  };

  const mapEvent = (event, references) => {
    const fields = fieldMap(event.fields);
    const sports = referenceNames(fields.sports, references);
    const countries = referenceNames(fields.country, references);
    const organizations = referenceNames(fields.organization, references);
    const fighters = referenceNames(fields.fighters, references);
    const dateStart = parseDate(fields.date_start);
    const dateEnd = parseDate(fields.date_end);
    const status = normalize(fields.status);
    const explicitlyHistoric = parseBoolean(fields.is_historic);
    const kind = explicitlyHistoric
      ? 'historic'
      : (dateStart && dateStart < today() ? 'past' : 'upcoming');

    return {
      name: fields.name || event.handle || 'Event',
      url: event.onlineStoreUrl || '',
      image: fields.resolved_image_url || '',
      dateStart,
      dateEnd,
      city: fields.city || '',
      location: referenceOrValue(fields.location_name, references) || referenceOrValue(fields.venue, references),
      country: countries[0] || '',
      countryCode: referenceCode(fields.country, references),
      sports,
      fighters,
      organization: organizations[0] || '',
      status,
      kind
    };
  };

  const renderEvent = (event, root) => {
    if (!event.url) return '';
    const day = event.dateStart ? event.dateStart.slice(8, 10) : '—';
    const monthDate = event.dateStart ? new Date(`${event.dateStart}T12:00:00`) : null;
    const month = monthDate ? monthDate.toLocaleDateString(root.dataset.language || 'nl-NL', { month: 'short' }) : 'TBA';
    const year = event.dateStart ? event.dateStart.slice(0, 4) : '';
    const location = [event.location, event.city].filter(Boolean).join(', ');
    const countryLine = event.country
      ? `<p class="f2w-event-card__location f2w-country-line">${countryFlag(event.countryCode, event.country)}${escapeHtml(event.country)}</p>`
      : '';
    const dateRange = event.dateEnd && event.dateEnd !== event.dateStart
      ? `<p class="f2w-event-card__date-range">${escapeHtml(root.dataset.labelUntil || 't/m')} ${escapeHtml(event.dateEnd.split('-').reverse().join('-'))}</p>`
      : '';
    const image = event.image
      ? `<img src="${escapeHtml(event.image)}" alt="${escapeHtml(event.name)}" width="1100" loading="lazy">`
      : `<div class="f2w-event-card__image--placeholder"><span>${escapeHtml(event.name.slice(0, 1))}</span></div>`;
    const sports = event.sports.length
      ? `<div class="f2w-event-card__sports">${event.sports.map((sport) => `<span class="f2w-event-card__sport">${escapeHtml(sport)}</span>`).join('')}</div>`
      : '';

    return `<article class="f2w-event-card" data-f2w-event-card data-name="${escapeHtml(normalize(event.name))}" data-city="${escapeHtml(normalize(event.city))}" data-country="${escapeHtml(normalize(event.country))}" data-sports="${escapeHtml(event.sports.map(normalize).join('|'))}" data-fighters="${escapeHtml(event.fighters.map(normalize).join('|'))}" data-organization="${escapeHtml(normalize(event.organization))}" data-status="${escapeHtml(event.status)}" data-kind="${event.kind}" data-date="${escapeHtml(event.dateStart)}">
      <a href="${escapeHtml(event.url)}" class="f2w-event-card__link">
        <div class="f2w-event-card__image">${image}</div>
        <div class="f2w-event-card__body">
          <div class="f2w-event-card__date-block"><span class="f2w-event-card__date-day">${escapeHtml(day)}</span><span class="f2w-event-card__date-month">${escapeHtml(month)}</span><span class="f2w-event-card__date-year">${escapeHtml(year)}</span></div>
          <div class="f2w-event-card__content">
            ${event.organization ? `<div class="f2w-event-card__topline"><span class="f2w-event-card__period f2w-event-card__period--upcoming" data-f2w-event-period-label>${escapeHtml(event.organization)}</span></div>` : ''}
            <h2 class="f2w-event-card__title">${escapeHtml(event.name)}</h2>
            ${dateRange}
            ${location ? `<p class="f2w-event-card__location">${escapeHtml(location)}</p>` : ''}
            ${countryLine}
            ${sports}
            <span class="f2w-event-card__view">${escapeHtml(root.dataset.labelView)}</span>
          </div>
        </div>
      </a>
    </article>`;
  };

  const setupDirectory = (root) => {
    const grid = root.querySelector('[data-f2w-event-results]');
    const tabs = [...root.querySelectorAll('[data-f2w-event-tab]')];
    const isArchive = root.dataset.isArchive === 'true';
    const searchInput = root.querySelector('[data-f2w-event-filter-search]');
    const countrySelect = root.querySelector('[data-f2w-event-filter-country]');
    const sportSelect = root.querySelector('[data-f2w-event-filter-sport]');
    const fighterSelect = root.querySelector('[data-f2w-event-filter-fighter]');
    const organizationSelect = root.querySelector('[data-f2w-event-filter-organization]');
    const pastCheckbox = root.querySelector('[data-f2w-event-filter-past]');
    const resetButton = root.querySelector('[data-f2w-event-filter-reset]');
    const countElement = root.querySelector('[data-f2w-event-results-count]');
    const emptyElement = root.querySelector('[data-f2w-event-empty]');
    const loadingElement = root.querySelector('[data-f2w-event-loading]');
    let activeView = root.dataset.defaultView || 'upcoming';
    let includePast = false;
    let isLoading = true;

    if (loadingElement) loadingElement.textContent = root.dataset.labelLoading || 'Events laden...';

    const readItems = () => [...root.querySelectorAll('[data-f2w-event-card]')].map((card) => ({
      card,
      name: normalize(card.dataset.name),
      city: normalize(card.dataset.city),
      country: normalize(card.dataset.country),
      sports: normalize(card.dataset.sports).split('|').filter(Boolean),
      fighters: normalize(card.dataset.fighters).split('|').filter(Boolean),
      organization: normalize(card.dataset.organization),
      status: normalize(card.dataset.status),
      kind: normalize(card.dataset.kind),
      date: card.dataset.date || '',
      periodLabel: card.querySelector('[data-f2w-event-period-label]')
    }));

    let items = readItems();

    const sortItemsChronologically = () => {
      items.sort((a, b) => {
        if (!a.date && !b.date) return 0;
        if (!a.date) return 1;
        if (!b.date) return -1;
        return a.date.localeCompare(b.date);
      });

      items.forEach((item) => grid?.appendChild(item.card));
    };

    const kindOf = (item) => item.kind || (item.date && item.date < today() ? 'past' : 'upcoming');
    const addOptions = (select, values, fallback) => {
      if (!select) return;
      select.innerHTML = `<option value="">${escapeHtml(fallback)}</option>` + values.map((value) => `<option value="${escapeHtml(value)}">${escapeHtml(filterLabel(value))}</option>`).join('');
    };

    const currentFilterValues = () => ({
      country: countrySelect?.value || '',
      sport: sportSelect?.value || '',
      fighter: fighterSelect?.value || '',
      organization: organizationSelect?.value || ''
    });

    const buildFilters = () => {
      const current = currentFilterValues();
      const configs = isArchive
        ? [
            {
              key: 'sport',
              select: sportSelect,
              fallback: root.dataset.labelAllSports,
              values: (item) => item.sports,
              matches: (item) => (!current.country || item.country === normalize(current.country)) && (!current.fighter || item.fighters.includes(normalize(current.fighter)))
            },
            {
              key: 'fighter',
              select: fighterSelect,
              fallback: root.dataset.labelAllFighters,
              values: (item) => item.fighters,
              labeler: titleCaseWords,
              matches: (item) => (!current.sport || item.sports.includes(normalize(current.sport))) && (!current.country || item.country === normalize(current.country))
            },
            {
              key: 'country',
              select: countrySelect,
              fallback: root.dataset.labelAllCountries,
              values: (item) => [item.country],
              labeler: titleCaseWords,
              matches: (item) => (!current.sport || item.sports.includes(normalize(current.sport))) && (!current.fighter || item.fighters.includes(normalize(current.fighter)))
            }
          ]
        : [
            {
              key: 'sport',
              select: sportSelect,
              fallback: root.dataset.labelAllSports,
              values: (item) => item.sports,
              matches: (item) => (!current.country || item.country === normalize(current.country)) && (!current.organization || item.organization === normalize(current.organization))
            },
            {
              key: 'country',
              select: countrySelect,
              fallback: root.dataset.labelAllCountries,
              values: (item) => [item.country],
              labeler: titleCaseWords,
              matches: (item) => (!current.sport || item.sports.includes(normalize(current.sport))) && (!current.organization || item.organization === normalize(current.organization))
            },
            {
              key: 'organization',
              select: organizationSelect,
              fallback: root.dataset.labelAllOrganizations,
              values: (item) => [item.organization],
              matches: (item) => (!current.sport || item.sports.includes(normalize(current.sport))) && (!current.country || item.country === normalize(current.country))
            }
          ];

      const filterItems = isArchive
        ? items.filter((item) => kindOf(item) === 'historic')
        : items;

      configs.forEach((config) => {
        const options = uniqueSorted(filterItems.filter(config.matches).flatMap(config.values), config.labeler);
        addOptions(config.select, options, config.fallback);
        const selectedKey = normalize(current[config.key]);
        const selectedOption = options.find((option) => normalize(option) === selectedKey);
        if (config.select && selectedOption) config.select.value = selectedOption;
      });
    };

    const setActiveTab = (view) => {
      activeView = view;
      tabs.forEach((tab) => {
        const active = tab.dataset.f2wEventTab === view;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
      });
    };

    const apply = (writeUrl = true) => {
      const search = normalize(searchInput?.value);
      const country = normalize(countrySelect?.value);
      const sport = normalize(sportSelect?.value);
      const fighter = normalize(fighterSelect?.value);
      const organization = normalize(organizationSelect?.value);
      let visible = 0;
      items.forEach((item) => {
        const text = [item.name, item.city, item.country, item.sports.join(' '), item.fighters.join(' '), item.organization, item.status].join(' ');
        const itemKind = kindOf(item);
        const matchesView = activeView === 'historic'
          ? itemKind === 'historic'
          : activeView === 'all' || includePast
            ? itemKind !== 'historic'
            : itemKind === 'upcoming';
        const matches = matchesView && (!search || text.includes(search)) && (!country || item.country === country) && (!sport || item.sports.includes(sport)) && (!fighter || item.fighters.includes(fighter)) && (!organization || item.organization === organization);
        item.card.hidden = !matches;
        if (matches) visible += 1;
      });
      if (countElement) countElement.textContent = `${visible} ${root.dataset.labelResults}`;
      if (emptyElement) emptyElement.hidden = isLoading || visible !== 0;
      if (writeUrl) {
        const url = new URL(window.location.href);
        [['view', activeView === root.dataset.defaultView ? '' : activeView], ['past', includePast ? '1' : ''], ['organization', organizationSelect?.value || ''], ['fighter', fighterSelect?.value || ''], ['q', searchInput?.value.trim() || ''], ['country', countrySelect?.value || ''], ['sport', sportSelect?.value || '']].forEach(([key, value]) => value ? url.searchParams.set(key, value) : url.searchParams.delete(key));
        window.history.replaceState({}, '', url);
      }
    };

    const readUrl = () => {
      const params = new URLSearchParams(window.location.search);
      const view = root.dataset.isArchive === 'true'
        ? 'historic'
        : (root.dataset.defaultView || 'upcoming');
      setActiveTab(view);
      if (searchInput) searchInput.value = params.get('q') || '';
      if (countrySelect) countrySelect.value = params.get('country') || '';
      if (sportSelect) sportSelect.value = params.get('sport') || '';
      if (fighterSelect) fighterSelect.value = params.get('fighter') || '';
      if (organizationSelect) organizationSelect.value = params.get('organization') || '';
      includePast = params.get('past') === '1';
      if (pastCheckbox) pastCheckbox.checked = includePast;
    };

    tabs.forEach((tab) => tab.addEventListener('click', () => { setActiveTab(tab.dataset.f2wEventTab || 'upcoming'); apply(); }));
    searchInput?.addEventListener('input', () => apply());
    countrySelect?.addEventListener('change', () => { buildFilters(); apply(); });
    sportSelect?.addEventListener('change', () => { buildFilters(); apply(); });
    fighterSelect?.addEventListener('change', () => { buildFilters(); apply(); });
    organizationSelect?.addEventListener('change', () => { buildFilters(); apply(); });
    pastCheckbox?.addEventListener('change', () => { includePast = Boolean(pastCheckbox.checked); apply(); });
    resetButton?.addEventListener('click', () => { if (searchInput) searchInput.value = ''; if (countrySelect) countrySelect.value = ''; if (sportSelect) sportSelect.value = ''; if (fighterSelect) fighterSelect.value = ''; if (organizationSelect) organizationSelect.value = ''; includePast = false; if (pastCheckbox) pastCheckbox.checked = false; buildFilters(); setActiveTab(root.dataset.defaultView || 'upcoming'); apply(); });
    window.addEventListener('popstate', () => { readUrl(); apply(false); });

    sortItemsChronologically();
    buildFilters();
    readUrl();
    buildFilters();
    apply(false);

    const setLoading = (loading) => {
      isLoading = loading;
      if (loadingElement) loadingElement.hidden = !loading;
    };

    if (!root.dataset.storefrontToken) {
      setLoading(false);
      apply(false);
      return;
    }

    const indexIds = readIndexEventIds(root);
    const loadEvents = async () => {
      if (indexIds) {
        try {
          return await loadIndexedEvents(root, indexIds);
        } catch (error) {
          console.warn('[FightBase] Published Event index unavailable. Falling back to the cursor crawl.', error);
        }
      }
      return loadAllEvents(root);
    };

    loadEvents().then(async (events) => {
      const references = await hydrateReferences(root, events);
      const mapped = sortEventsChronologically(
        events
          .map((event) => mapEvent(event, references))
          .filter((event) => event.url)
      );

      setLoading(false);
      if (!mapped.length) {
        apply(false);
        return;
      }

      grid.innerHTML = mapped
        .map((event) => renderEvent(event, root))
        .join('');
      items = readItems();
      sortItemsChronologically();
      buildFilters();
      readUrl();
      buildFilters();
      apply(false);
    }).catch((error) => {
      setLoading(false);
      apply(false);
      console.warn('[FightBase] Events API unavailable. Showing the empty event state.', error);
    });
  };

  roots.forEach(setupDirectory);
})();

